import { TERMS, AGREEING } from './lexicon';

/**
 * Resolves terminology tokens in locale strings into concrete text, at load /
 * tenant-merge time (NOT per `$t` call). Because expansion runs before vue-i18n
 * ever parses a message, the `|` inside an agreement token (e.g. `{active|opportunity}`)
 * is gone before vue-i18n's pluralization splitter runs — so it never collides with
 * the top-level `singular | plural` separator.
 *
 * Tokens:
 *   {term:form}              noun form, e.g. {opportunity:the_singular} -> "l'opportunité"
 *   {word|term}             agreeing word (singular), e.g. {active|opportunity} -> "active"
 *   {word|term:plural}      agreeing word (plural),   e.g. {external|opportunity:plural} -> "externes"
 *
 * Optional trailing case modifier — `:cap` (first letter), `:upper`, `:lower`. It
 * capitalizes the first character of the *resolved phrase* (often an article), which is
 * exactly what sentence-initial position needs:
 *   {opportunity:this_singular:cap} -> "Cette opportunité"
 *   {opportunity:singular:upper}    -> "OPPORTUNITÉ"
 *   {created|opportunity:cap}       -> "Créé"
 *
 * Identifiers are always written lowercase; case is controlled only by the modifier.
 * Unknown terms/words/forms are left untouched, so real params like {count} and
 * value-expression text like {{opportunity.endDate}} are never disturbed.
 */

const AGREEING_TOKEN = /\{([A-Za-z][A-Za-z0-9_]*)\|([A-Za-z][A-Za-z0-9_]*)((?::[a-z]+)*)\}/g;
const NOUN_TOKEN = /\{([A-Za-z][A-Za-z0-9_]*):([a-z][a-z0-9_]*)((?::[a-z]+)*)\}/g;

const NUMBER_MODS = { singular: true, plural: true };
const CASE_MODS = { cap: true, upper: true, lower: true };

function parseModifiers(raw) {
  const mods = { number: 'singular', case: null };
  if (!raw) {
    return mods;
  }
  for (const part of raw.split(':')) {
    if (NUMBER_MODS[part]) {
      mods.number = part;
    } else if (CASE_MODS[part]) {
      mods.case = part;
    }
  }
  return mods;
}

function applyCase(str, mode) {
  switch (mode) {
    case 'cap':
      return str.length ? str.charAt(0).toUpperCase() + str.slice(1) : str;
    case 'upper':
      return str.toUpperCase();
    case 'lower':
      return str.toLowerCase();
    default:
      return str;
  }
}

/**
 * Picks the agreeing form for `word` against `term`. Beyond the ms/fs/mp/fp grid, a masculine
 * singular word may carry an OPTIONAL `msv` form used before a vowel-sound noun
 * (nouveau -> nouvel, beau -> bel, vieux -> vieil). It is consulted only when the term begins
 * with a vowel sound (`term.startsWithVowelSound`) AND the word actually defines `msv`; otherwise we
 * fall back to `ms`. So only the few elidable pre-posed adjectives need to declare `msv`.
 */
function pickAgreementForm(term, word, number) {
  const masculine = term.gender !== 'f';
  const plural = number === 'plural';
  if (masculine && !plural && term.startsWithVowelSound && word.msv != null) {
    return word.msv;
  }
  return word[(masculine ? 'm' : 'f') + (plural ? 'p' : 's')];
}

export function expandString(str, terms, agreeing) {
  if (typeof str !== 'string' || str.indexOf('{') === -1) {
    return str;
  }

  let out = str.replace(AGREEING_TOKEN, (match, wordKey, termKey, rawMods) => {
    const term = terms[termKey.toLowerCase()];
    const word = agreeing[wordKey.toLowerCase()];
    if (!term || !word) {
      return match;
    }
    const mods = parseModifiers(rawMods);
    const form = pickAgreementForm(term, word, mods.number);
    if (form == null) {
      return match;
    }
    return applyCase(form, mods.case);
  });

  out = out.replace(NOUN_TOKEN, (match, termKey, formKey, rawMods) => {
    const term = terms[termKey.toLowerCase()];
    const form = term && term.forms ? term.forms[formKey] : undefined;
    if (form == null) {
      return match;
    }
    return applyCase(form, parseModifiers(rawMods).case);
  });

  return out;
}

function expandNode(node, terms, agreeing) {
  for (const key of Object.keys(node)) {
    const value = node[key];
    if (typeof value === 'string') {
      node[key] = expandString(value, terms, agreeing);
    } else if (value && typeof value === 'object') {
      expandNode(value, terms, agreeing);
    }
  }
}

/**
 * Expands every locale message tree in place using the default lexicon.
 * Called once at bootstrap. `messages` is mutated and returned.
 */
export function expandTerminology(messages, termsByLocale = TERMS, agreeingByLocale = AGREEING) {
  for (const locale of Object.keys(messages)) {
    const terms = termsByLocale[locale];
    const agreeing = agreeingByLocale[locale];
    if (!terms || !agreeing) {
      continue;
    }
    expandNode(messages[locale], terms, agreeing);
  }
  return messages;
}

/**
 * Merges a tenant's term overrides over the defaults (deep, per form), leaving the
 * shared AGREEING table untouched. Returns a new TERMS-shaped object. Used when the
 * backend tenant config arrives; the caller re-expands raw templates with the result.
 */
export function mergeTenantTerms(tenantTermsByLocale, defaults = TERMS) {
  const merged = {};
  for (const locale of Object.keys(defaults)) {
    merged[locale] = {};
    const baseLocale = defaults[locale];
    const overrideLocale = (tenantTermsByLocale && tenantTermsByLocale[locale]) || {};
    for (const termKey of Object.keys(baseLocale)) {
      const base = baseLocale[termKey];
      const override = overrideLocale[termKey];
      merged[locale][termKey] = override
        ? { ...base, ...override, forms: { ...base.forms, ...(override.forms || {}) } }
        : base;
    }
  }
  return merged;
}

/**
 * Builds a fresh set of expanded locale messages from the kept (tokenized) raw templates and
 * a tenant's term overrides. Pure: deep-clones internally so `rawByLocale` is never mutated,
 * which is what lets the same templates be re-expanded whenever the tenant overlay changes.
 * Pass `null`/`undefined` for `tenantTermsByLocale` to expand with the default lexicon.
 */
export function buildTenantMessages(rawByLocale, tenantTermsByLocale) {
  const mergedTerms = mergeTenantTerms(tenantTermsByLocale);
  const expanded = JSON.parse(JSON.stringify(rawByLocale));
  expandTerminology(expanded, mergedTerms);
  return expanded;
}
